/**
 * <RegulatedOnly>
 *
 * Renders children only when the active org is in regulated mode.
 * Optionally also requires a permission flag from useComplianceMode().
 *
 * Use this to wrap any UI element that should be hidden in standard mode:
 *
 *   <RegulatedOnly>
 *     <ApprovalStatusPanel />
 *   </RegulatedOnly>
 */

import { ReactNode } from 'react';
import { useComplianceMode } from '@/hooks/useComplianceMode';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  requirePermission?: 'manage' | 'approve' | 'override';
  /** When true, the children render in standard mode too (for warning-style UI). */
  alwaysRender?: boolean;
}

export function RegulatedOnly({
  children,
  fallback = null,
  requirePermission,
  alwaysRender = false,
}: Props) {
  const { isRegulated, canManageCompliance, canApproveContent, canOverrideBlock } =
    useComplianceMode();

  if (!alwaysRender && !isRegulated) return <>{fallback}</>;

  if (requirePermission === 'manage' && !canManageCompliance) return <>{fallback}</>;
  if (requirePermission === 'approve' && !canApproveContent) return <>{fallback}</>;
  if (requirePermission === 'override' && !canOverrideBlock) return <>{fallback}</>;

  return <>{children}</>;
}
